import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Eye, EyeOff, Loader2, Pencil, X } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { PersonRow } from '../workspace/WorkspaceKit';
import { useSaveOnboardingIdentity } from '../../../services/clientOnboarding';
import BirthdayCalendar from './BirthdayCalendar';

// Birthday and SSN in About them (Nic 2026-10-25). The VA check calls the VA
// with exactly these, and a birthday typed wrong at sign-up is the usual
// reason the VA "didn't recognize" them. Dotted out; the eye shows one so
// their specialist can ask the veteran to confirm it, and it hides again
// after 30 seconds. The pencil fixes them. Only their specialist or the
// Onboarding Manager (`identity.canSee`); every reveal and change is in
// History by who and when, never with the value. A revealed value is
// fetched each time and never cached.

export const HIDE_AFTER_MS = 30 * 1000;

const ssnDigits = (v) =>
  String(v || '')
    .replace(/\D/g, '')
    .slice(0, 9);
const ssnShape = (d) =>
  d.length > 5
    ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`
    : d.length > 3
      ? `${d.slice(0, 3)}-${d.slice(3)}`
      : d;
// A birthday is typed, never picked (Nic 2026-10-25: the browser's date
// picker was a calendar to scroll back decades in). MM/DD/YYYY, the slashes
// added as they type; saved as YYYY-MM-DD.
const dobDigits = (v) =>
  String(v || '')
    .replace(/\D/g, '')
    .slice(0, 8);
const dobShape = (d) =>
  d.length > 4
    ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
    : d.length > 2
      ? `${d.slice(0, 2)}/${d.slice(2)}`
      : d;
const dobToYmd = (typed) => {
  const d = dobDigits(typed);
  return d.length === 8
    ? `${d.slice(4)}-${d.slice(0, 2)}-${d.slice(2, 4)}`
    : typed;
};
// The latest birthday that can sign up: 17 years ago today (the server's
// rule, oldEnoughToSignUp).
const latestDob = () => {
  const now = new Date();
  return `${now.getFullYear() - 17}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const dobShow = (ymd) =>
  ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)
    ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}`
    : ymd || '';

const ICON_BTN =
  'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50 dark:hover:!bg-white/10 dark:hover:text-slate-200';

const IdentityRow = ({ onboardingId, field, label, masked, has, canSee }) => {
  const [shown, setShown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState(null);
  const timer = useRef(null);
  const save = useSaveOnboardingIdentity();

  const hide = () => {
    clearTimeout(timer.current);
    setShown(null);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  // Another client: never carry a revealed value over.
  useEffect(() => {
    hide();
    setEditing(false);
  }, [onboardingId]);

  const reveal = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await axios.get(
        `/client-onboarding/${onboardingId}/identity/${field}`,
      );
      setShown(res.data?.value || '');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setShown(null), HIDE_AFTER_MS);
      return res.data?.value || '';
    } catch (e) {
      setErr(e?.response?.data?.msg || "That couldn't be shown.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const startEdit = async () => {
    // Start from what's on file, so a one-digit fix is a one-digit fix.
    const value = shown ?? (has ? await reveal() : '');
    if (value === null) return;
    setDraft(
      field === 'ssn' ? ssnShape(ssnDigits(value)) : dobShow(value || ''),
    );
    setEditing(true);
  };

  const onSave = async () => {
    setErr(null);
    try {
      await save.mutateAsync({
        onboardingId,
        [field]: field === 'ssn' ? ssnDigits(draft) : dobToYmd(draft),
      });
      setEditing(false);
      hide();
    } catch (e) {
      setErr(e?.response?.data?.msg || "That couldn't be saved.");
    }
  };

  if (editing) {
    const ready = field === 'ssn' ? ssnDigits(draft).length === 9 : dobDigits(draft).length === 8;
    const cancel = () => {
      setEditing(false);
      setErr(null);
    };
    return (
      <div>
        {/* In place (Nic 2026-10-25): the same row, the box where the value
            was, the calendar inside the box, and a red X and a green check
            where the eye and pencil were. Nothing moves. */}
        <div className="flex items-center gap-[2px]">
          <div className="-mx-[8px] flex min-w-0 flex-1 items-center justify-between gap-[8px] px-[8px] py-[1px]">
            <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{label}</span>
            <span className="relative">
              <input
                aria-label={label}
                autoFocus
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder={field === 'ssn' ? '123-45-6789' : 'MM/DD/YYYY'}
                value={draft}
                onChange={(e) =>
                  setDraft(field === 'ssn' ? ssnShape(ssnDigits(e.target.value)) : dobShape(dobDigits(e.target.value)))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ready) onSave();
                  if (e.key === 'Escape') {
                    // Backs out of the edit only, not the Workspace (QA 2026-10-26).
                    e.stopPropagation();
                    cancel();
                  }
                }}
                className={cn(
                  'h-[24px] rounded-md border border-slate-200 bg-white pl-[7px] text-right text-[13px] font-medium tabular-nums text-slate-800 shadow-[inset_0_1px_1px_rgba(15,23,42,0.04)] outline-none transition-[border-color,box-shadow] placeholder:font-normal placeholder:text-slate-300 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 dark:border-white/15 dark:!bg-white/5 dark:text-slate-100 dark:placeholder:text-slate-600',
                  field === 'dob' ? 'w-[124px] pr-[26px]' : 'w-[112px] pr-[7px]',
                )}
              />
              {field === 'dob' ? (
                <span className="absolute inset-y-0 right-[3px] flex items-center">
                  <BirthdayCalendar value={dobToYmd(draft)} max={latestDob()} onPick={(day) => setDraft(dobShow(day))} />
                </span>
              ) : null}
            </span>
          </div>
          <button
            type="button"
            aria-label="Cancel"
            title="Cancel (Esc)"
            onClick={cancel}
            className={cn(ICON_BTN, 'text-rose-500 hover:bg-rose-50 hover:text-rose-600 dark:text-rose-300 dark:hover:!bg-rose-500/10')}
          >
            <X size={14} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            aria-label="Save"
            title="Save (Enter)"
            disabled={save.isLoading || !ready}
            onClick={onSave}
            className={cn(ICON_BTN, 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-300 dark:hover:!bg-emerald-500/10')}
          >
            {save.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
          </button>
        </div>
        {err ? <div className="mt-[2px] pr-[48px] text-right text-[11.5px] text-rose-600 dark:text-rose-300">{err}</div> : null}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-[2px]">
        <div className="min-w-0 flex-1">
          <PersonRow label={label} muted={!has}>
            {!has
              ? 'None on file'
              : shown !== null
                ? field === 'dob'
                  ? dobShow(shown)
                  : shown
                : masked}
          </PersonRow>
        </div>
        {canSee && has ? (
          <button
            type="button"
            onClick={() => (shown !== null ? hide() : reveal())}
            disabled={loading}
            aria-label={
              shown !== null
                ? `Hide their ${label.toLowerCase()}`
                : `Show their ${label.toLowerCase()}`
            }
            title={
              shown !== null
                ? 'Hide'
                : 'Show it, to confirm with them (logged in History)'
            }
            className={ICON_BTN}
          >
            {loading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : shown !== null ? (
              <EyeOff size={13} />
            ) : (
              <Eye size={13} />
            )}
          </button>
        ) : null}
        {canSee ? (
          <button
            type="button"
            onClick={startEdit}
            disabled={loading}
            aria-label={`Change their ${label.toLowerCase()}`}
            title="Fix it (logged in History)"
            className={ICON_BTN}
          >
            <Pencil size={12} />
          </button>
        ) : null}
      </div>
      {err ? (
        <div
          className={cn(
            'text-right text-[11.5px] text-rose-600 dark:text-rose-300',
          )}
        >
          {err}
        </div>
      ) : null}
    </div>
  );
};

const IdentityRows = ({ onboardingId, identity }) => {
  if (!identity || (!identity.canSee && !identity.hasSsn && !identity.hasDob))
    return null;
  return (
    <>
      <IdentityRow
        onboardingId={onboardingId}
        field="dob"
        label="Birthday"
        masked="••/••/••••"
        has={identity.hasDob}
        canSee={identity.canSee}
      />
      <IdentityRow
        onboardingId={onboardingId}
        field="ssn"
        label="SSN"
        masked={
          identity.ssnLast4 ? `•••-••-${identity.ssnLast4}` : '•••-••-••••'
        }
        has={identity.hasSsn}
        canSee={identity.canSee}
      />
    </>
  );
};

export default IdentityRows;
